import { useState, useCallback, useMemo, useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Menu } from 'lucide-react';
import { Button } from './components/ui/button';
import { cn } from './components/ui/utils';
import { AppSidebar } from './components/AppSidebar';
import { Sheet, SheetContent, SheetTitle } from './components/ui/sheet';
import { UserProfileMenu } from './components/UserProfileMenu';
import { NewsMonitorPage } from './components/RelevantNewsPage';
import { NewsDetailSidebar } from './components/NewsDetailSidebar';
import type { NewsArticle } from './components/RelevantNewsPage';
import { DashboardsPage } from './components/DashboardsPage';
import { BrandPulsePage } from './components/BrandPulsePage';
import { MarketingMixModelPage } from './components/MarketingMixModelPage';
import { isEmbedMode } from '@/utils/urlParams';
import { useQuery } from '@tanstack/react-query';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { clientPagesFor, isClientAllowedPage } from '@/cowork/clientFeatures';
import { StaffScopeBar } from '@/cowork/StaffScopeBar';
import { CMDesk } from '@/cowork/cm/CMDesk';
import { MovedToBackOffice } from '@/cowork/shared/MovedToBackOffice';
import { CompetitorAdsPage } from '@/cowork/discover/CompetitorAdsPage';
import { AdsLibraryPage } from '@/cowork/discover/AdsLibraryPage';
import { RadarLibraryPage } from '@/cowork/discover/RadarLibraryPage';
import { CampaignCanvasPage } from '@/cowork/canvas/CampaignCanvasPage';
import { TikTokTrendsPage } from '@/cowork/discover/TikTokTrendsPage';
import { KnowledgePage } from '@/cowork/knowledge/KnowledgePage';
import { CreatorsListPage } from './components/CreatorsListPage';
import { CreatorListDetailPage } from './components/CreatorListDetailPage';
import { DiscoveryEmbedPage } from './components/DiscoveryEmbedPage';
import { InsightsPage } from './components/InsightsPage';
import { TrendsPage } from './components/TrendsPage';
const SeeCampaignsPage = lazy(() =>
  import('./components/SeeCampaignsPage').then((m) => ({ default: m.SeeCampaignsPage })),
);
import { CampaignCalendarPage } from './components/CampaignCalendarPage';
import { AmbassadorsPage } from './components/AmbassadorsPage';
import { DraftsPage } from './components/DraftsPage';
import { CastingPage } from './components/CastingPage';
import { ContentHubPage } from './components/ContentHubPageNew';
import { KeepMounted } from './components/KeepMounted';
import { StrategiesPage } from './components/StrategiesPage';
import { MessagesPage } from '@/cowork/comms/MessagesPage';
import { BrandLiftPage } from './components/BrandLiftPage';
import { DashboardPage } from './components/DashboardPage';
import { VideoAdStudioPage } from './components/VideoAdStudioPage';
import { CreatorProfileSidebar, CreatorProfile } from './components/CreatorProfileSidebar';
import { AdStudioLandingPage } from './components/AdStudioLandingPage';
import { DubbingPage } from './components/DubbingPage';
import { AudioStudioGate } from './components/audioStudio/AudioStudioGate';
import { SettingsPage } from './components/SettingsPage';
import { TeamPage } from './components/TeamPage';
import { AdAccountsPage } from './components/AdAccountsPage';
import { TrophyRoomPage } from './components/TrophyRoomPage';
import { DevModePage } from './components/DevModePage';
import { TopNavBar } from './components/TopNavBar';
import { ChatPage } from './components/ChatPage';
import {
  parseAppPath,
  getPathForPage,
  getPathForMode,
  isCoworkGatedPlatformPage,
  isAgentPage,
  AGENT_PAGE_VIEW,
  isPreviewGatedPlatformPage,
  normalizeCampaignSubTabSegment,
  type CurrentPage,
  type AppMode,
} from '@/routes';
import { AgentSurface } from '@/cowork/AgentSurface';
import { ReportsPage } from '@/cowork/reports/ReportsPage';
import { VideoStudioPage } from '@/cowork/video/VideoStudioPage';
import { DeliverablesPage } from '@/cowork/DeliverablesPage';
import { CampaignWizard } from '@/cowork/wizard/CampaignWizard';
import { AgentRailProvider, useAgentRail } from '@/cowork/rail/AgentRailContext';
import { AgentRail } from '@/cowork/rail/AgentRail';
import { useAuth } from '@/contexts/AuthContext';
import {
  LAST_CAMPAIGN_NAV_SYNC_EVENT,
  persistLastCampaignPathIfNeeded,
  readLastCampaignPath,
  readLastCampaignDisplayName,
} from '@/utils/lastCampaignPath';

export type Song = {
  id?: string;
  coverUrl: string;
  title: string;
  artist: string;
  duration: number;
  bpm: number;
  tempo?: number;
  mood: string;
  genre?: string;
  tags: string[];
  featured?: boolean;
  audioUrl?: string;
};

export default function App() {
  return (
    <AgentRailProvider>
      <AppShell />
    </AgentRailProvider>
  );
}

function AppShell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Local dev override: VITE_COWORK_FORCE=true (in .env.local) shows the
  // agent layer for accounts without the show_cowork entitlement. Dev-only.
  const showCowork = user?.show_cowork === true
    || (import.meta.env.DEV && import.meta.env.VITE_COWORK_FORCE === 'true' && !!user);
  const showEvolve = user?.show_evolve === true;
  // Clients (no show_cowork) may still open the agent pages Beatly has opened for their team
  // (GET /v1/me → features, backend engine/clientFeatures.ts). /me is exempt from the staff gate.
  const clientMe = useQuery({ queryKey: ['cowork', 'me'], queryFn: () => coworkApi.staffScope.me(), enabled: !!user && !showCowork, retry: false, staleTime: 5 * 60_000 });
  const clientPages = useMemo(() => (showCowork ? [] : clientPagesFor(clientMe.data?.features)), [showCowork, clientMe.data?.features]);
  const clientPagesPending = !showCowork && !!user && clientMe.isPending;
  const { open: railOpen, available: railAvailable } = useAgentRail();
  /* The rail is a fixed panel; give the canvas room so it never covers content.
     Not on the agent pages themselves — those already are the agent surface. */
  const railDocked = showCowork && railAvailable && railOpen;
  const userName = user?.name?.trim() || user?.first_name?.trim() || t('common.guest');
  const dashboardAccountManager = user?.is_client ? user?.team?.account_manager : undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{
    creatorId?: string;
    listId?: string;
    campaignId?: string;
    subTab?: string;
    beatId?: string;
    trophyId?: string;
    contentHubMediaId?: string;
  }>();

  const routeParams = useMemo(() => {
    const parsed = parseAppPath(location.pathname);
    const subTabRaw = params.subTab ?? parsed.subTab;
    return {
      ...parsed,
      creatorId: params.creatorId ?? parsed.creatorId,
      listId: params.listId ?? parsed.listId,
      campaignId: params.campaignId ?? parsed.campaignId,
      subTab:
        subTabRaw === undefined ? undefined : normalizeCampaignSubTabSegment(subTabRaw),
      beatId: params.beatId ?? parsed.beatId,
      trophyId: params.trophyId ?? parsed.trophyId,
      contentHubMediaId: params.contentHubMediaId ?? parsed.contentHubMediaId,
    };
  }, [
    location.pathname,
    params.creatorId,
    params.listId,
    params.campaignId,
    params.subTab,
    params.beatId,
    params.trophyId,
    params.contentHubMediaId,
  ]);

  const { activeMode, currentPage, listId, campaignId, subTab, beatId, trophyId, contentHubMediaId } = routeParams;
  /* Hard gate: without show_cowork the agent/cowork pages must never mount —
     not even for the one frame before the redirect effect below fires (a
     mounted page fires its cowork API calls with the customer's token). */
  const coworkBlocked =
    !showCowork &&
    !isClientAllowedPage(currentPage, clientPages) &&
    (activeMode === 'chat' || activeMode === 'cowork' || isCoworkGatedPlatformPage(currentPage));

  useLayoutEffect(() => {
    const path = location.pathname;
    const legacyResultsReport = /^(\/campaigns\/[^/]+)\/results\/report(\/.*)?$/;
    let m = legacyResultsReport.exec(path);
    if (m) {
      const next = `${m[1]}/results${m[2] ?? ''}${location.search}${location.hash}`;
      navigate(next, { replace: true });
      return;
    }
    const legacySuggestions = /^(\/campaigns\/[^/]+)\/suggestions(\/.*)?$/;
    m = legacySuggestions.exec(path);
    if (m) {
      const next = `${m[1]}/profiles${m[2] ?? ''}${location.search}${location.hash}`;
      navigate(next, { replace: true });
      return;
    }
    /** Legacy singular segment `/collaboration` → canonical `/collaborations` (Publications lives at `/publications`). */
    const legacyCollaborationSingular = /^(\/campaigns\/[^/]+)\/collaboration(\/.*)?$/;
    m = legacyCollaborationSingular.exec(path);
    if (m) {
      const next = `${m[1]}/collaborations${m[2] ?? ''}${location.search}${location.hash}`;
      navigate(next, { replace: true });
      return;
    }
    const legacyCampaignDrafts = /^(\/campaigns\/[^/]+)\/drafts(\/.*)?$/;
    m = legacyCampaignDrafts.exec(path);
    if (m) {
      const next = `${m[1]}/content-approval${m[2] ?? ''}${location.search}${location.hash}`;
      navigate(next, { replace: true });
      return;
    }
    const legacyCampaignSummaryReport = /^(\/campaigns\/[^/]+)\/summary\/report(\/.*)?$/;
    m = legacyCampaignSummaryReport.exec(path);
    if (m) {
      const next = `${m[1]}/results${m[2] ?? ''}${location.search}${location.hash}`;
      navigate(next, { replace: true });
      return;
    }
    const legacyCampaignSummary = /^(\/campaigns\/[^/]+)\/summary(\/.*)?$/;
    m = legacyCampaignSummary.exec(path);
    if (m) {
      const next = `${m[1]}/results${m[2] ?? ''}${location.search}${location.hash}`;
      navigate(next, { replace: true });
      return;
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    // Cowork-gated platform pages and chat/cowork modes require show_cowork.
    if (!showCowork) {
      if (activeMode === 'chat' || activeMode === 'cowork') {
        navigate('/', { replace: true });
        return;
      }
      if (isCoworkGatedPlatformPage(currentPage) && !isClientAllowedPage(currentPage, clientPages)) {
        if (clientPagesPending) return; // the team's open features are still loading — decide once they are known
        navigate('/', { replace: true });
        return;
      }
    }
    // Trends + Evolve are staff-only when show_evolve is false.
    if (!showEvolve && isPreviewGatedPlatformPage(currentPage)) {
      navigate('/', { replace: true });
    }
  }, [showCowork, showEvolve, activeMode, currentPage, navigate, clientPages, clientPagesPending]);

  useEffect(() => {
    // Content Hub is a split-pane view; prevent page-level scrolling so panes scroll independently.
    if (currentPage === 'content-hub') {
      document.body.classList.add('content-hub-lock-scroll');
      document.documentElement.classList.add('content-hub-lock-scroll');
    }
    return () => {
      document.body.classList.remove('content-hub-lock-scroll');
      document.documentElement.classList.remove('content-hub-lock-scroll');
    };
  }, [currentPage]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [lastCampaignPath, setLastCampaignPath] = useState<string | null>(() => readLastCampaignPath());
  const [lastCampaignDisplayName, setLastCampaignDisplayName] = useState<string | null>(() =>
    readLastCampaignDisplayName()
  );
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [selectedCreator, setSelectedCreator] = useState<CreatorProfile | null>(null);
  const [creatorProfileOpen, setCreatorProfileOpen] = useState(false);

  useEffect(() => {
    persistLastCampaignPathIfNeeded(location.pathname);
    setLastCampaignPath(readLastCampaignPath());
    setLastCampaignDisplayName(readLastCampaignDisplayName());
  }, [location.pathname]);

  useEffect(() => {
    const sync = () => {
      setLastCampaignPath(readLastCampaignPath());
      setLastCampaignDisplayName(readLastCampaignDisplayName());
    };
    window.addEventListener(LAST_CAMPAIGN_NAV_SYNC_EVENT, sync);
    return () => window.removeEventListener(LAST_CAMPAIGN_NAV_SYNC_EVENT, sync);
  }, []);

  const handleNavigate = useCallback(
    (page: string, creatorIdArg?: string) => {
      if (page === 'last-campaign') {
        const path = readLastCampaignPath();
        navigate(path ?? '/campaigns');
        return;
      }
      const p = page as CurrentPage;
      if (p === 'creator-profile' && creatorIdArg) {
        navigate(`/creators/${creatorIdArg}`);
        return;
      }
      /** All Campaigns / sidebar: list lives at `/campaigns` — do not merge current campaign detail params. */
      if (p === 'see-campaigns') {
        navigate(getPathForPage('see-campaigns'));
        return;
      }
      navigate(getPathForPage(p, {
        creatorId: creatorIdArg,
        listId: listId,
        campaignId: campaignId,
        subTab,
        beatId,
        trophyId,
      }));
    },
    [navigate, listId, campaignId, subTab, beatId, trophyId]
  );

  const handleModeChange = useCallback(
    (mode: AppMode) => {
      navigate(getPathForMode(mode));
    },
    [navigate]
  );

  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([
    {
      id: 1,
      title: 'Instagram Launches New Creator Marketplace Features',
      summary: 'Meta announces a comprehensive update to Instagram\'s creator marketplace, introducing advanced matching algorithms and enhanced brand partnership tools.',
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=800',
      sourceName: 'Social Media Today',
      sourceLogo: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
      relevancyScore: 95,
      isRead: false,
      category: 'Platform Updates',
      publishedDate: '2 hours ago',
      fullContent: 'Meta has announced a major update to Instagram\'s Creator Marketplace, introducing new features designed to streamline brand-creator partnerships. The update includes advanced AI-powered matching algorithms, enhanced analytics tools, and new communication features. Brands can now access more detailed creator insights, including audience demographics, engagement patterns, and content performance metrics. The update also introduces a new contract management system and payment processing tools, making it easier for brands to manage multiple creator partnerships simultaneously.',
      url: '#'
    },
    {
      id: 2,
      title: 'TikTok UGC Campaigns See 45% Higher Engagement',
      summary: 'New research reveals that user-generated content campaigns on TikTok are significantly outperforming traditional branded content.',
      image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=800',
      sourceName: 'Marketing Dive',
      sourceLogo: 'https://images.unsplash.com/photo-1611162617263-4ec3f9ac58c4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
      relevancyScore: 88,
      isRead: false,
      category: 'Industry Trends',
      publishedDate: '5 hours ago',
      fullContent: 'A comprehensive study analyzing thousands of TikTok campaigns has revealed that user-generated content (UGC) campaigns achieve 45% higher engagement rates compared to traditional branded content. The research highlights that authenticity and relatability are key drivers of success on the platform. Brands that successfully leverage UGC often see increased brand loyalty, higher conversion rates, and more organic reach. The study also found that campaigns featuring diverse creators and authentic storytelling perform significantly better than those with highly polished, traditional advertising approaches.',
      url: '#'
    }
  ]);

  const handleToggleRead = useCallback((articleId: number) => {
    setNewsArticles(prev =>
      prev.map(article =>
        article.id === articleId
          ? { ...article, isRead: true }
          : article
      )
    );
  }, []);

  const handleCloseCreatorProfile = () => {
    setCreatorProfileOpen(false);
    setTimeout(() => {
      setSelectedCreator(null);
    }, 300);
  };

  const handleSidebarExpandedChange = useCallback((expanded: boolean) => {
    setSidebarExpanded(expanded);
  }, []);

  return (
    <div
      className={cn(
        currentPage === 'content-hub'
          ? 'h-screen overflow-hidden bg-white flex flex-col'
          : 'min-h-screen bg-white flex flex-col',
        // Below 1440px the rail overlays instead of squeezing pages (the wizard
        // was unusable at 1280 with 380px carved off). Content padding only on
        // wide screens; the rail itself stays fixed and floats above otherwise. In the back office frame there is no
        // Omni sidebar, so the same room for the page arrives at a narrower width (1440 minus the sidebar it lacks).
        railDocked && (isEmbedMode() ? 'min-[1180px]:pr-[380px]' : 'min-[1440px]:pr-[380px]'),
      )}
    >
      {/* Top Navigation Bar – full width, always on top. Hidden in embed mode:
          the hosting shell (Beatly BO) provides navigation. */}
      {!isEmbedMode() && (
      <div className="hidden lg:block fixed top-0 left-0 right-0 z-50">
        <TopNavBar
          currentPage={currentPage}
          onPageChange={handleNavigate}
          activeMode={activeMode}
          onModeChange={handleModeChange}
          showCowork={showCowork}
          lastCampaignPath={lastCampaignPath}
          lastCampaignDisplayName={lastCampaignDisplayName}
        />
      </div>
      )}

      {/* Mobile Header */}
      {!isEmbedMode() && (
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center justify-between px-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(true)}
          aria-label={t('nav.openMobileMenu')}
        >
          <Menu className="w-6 h-6" aria-hidden />
        </Button>
        <UserProfileMenu onPageChange={handleNavigate} />
      </div>
      )}

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 gap-0 flex flex-col w-[min(100vw,20rem)] sm:max-w-sm [&_[data-slot=sheet-close]]:z-10">
          <SheetTitle className="sr-only">{t('nav.mobileNavTitle')}</SheetTitle>
          <div className="flex-1 min-h-0 overflow-auto pt-12">
            <AppSidebar
              isMobile
              isCollapsed
              onToggle={() => {}}
              currentPage={currentPage}
              onPageChange={handleNavigate}
              onNavigate={() => setMobileMenuOpen(false)}
              showCowork={showCowork}
              clientPages={clientPages}
              lastCampaignPath={lastCampaignPath}
              lastCampaignDisplayName={lastCampaignDisplayName}
              pathname={location.pathname}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Below top bar: sidebar + content */}
      <div className={`flex flex-1 min-h-0 ${isEmbedMode() ? 'pt-0' : 'pt-16 lg:pt-14'} ${currentPage === 'content-hub' ? 'overflow-hidden' : ''}`}>
        {activeMode === 'chat' && !coworkBlocked ? (
          <div className="flex-1">
            <ChatPage />
          </div>
        ) : (
        <>
        {/* Desktop Sidebar — hidden in embed mode (host shell navigates) */}
        {!isEmbedMode() && (
        <div className="hidden lg:block">
          <AppSidebar
            isCollapsed={true}
            onToggle={() => {}}
            currentPage={currentPage}
            onPageChange={handleNavigate}
            onExpandedChange={handleSidebarExpandedChange}
            showCowork={showCowork}
            clientPages={clientPages}
            lastCampaignPath={lastCampaignPath}
            lastCampaignDisplayName={lastCampaignDisplayName}
            pathname={location.pathname}
          />
        </div>
        )}

        {/* Page Content — grey canvas with concave corner */}
        <div
          className={`flex-1 min-w-0 overflow-x-hidden transition-all duration-300 relative ${isEmbedMode() ? '' : sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}`}
        >
          {/* Concave corner cutout — top-left of grey canvas */}
          <div className="hidden lg:block absolute top-0 left-0 z-10 pointer-events-none" style={{ width: 24, height: 24 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 0H0V24C0 10.745 10.745 0 24 0Z" fill="white" />
            </svg>
          </div>
          {/* Grey canvas background */}
          <div
            className={
              currentPage === 'content-hub'
                ? 'h-full min-h-0 overflow-hidden bg-[var(--background)]'
                : `${isEmbedMode() ? 'min-h-screen' : 'min-h-[calc(100vh-3.5rem)]'} bg-[var(--background)]`
            }
          >
            {/* Beatly staff only: which client workspace this session acts in. */}
            {/* Back office only: in the omni app the top-right team switch is the client picker. */}
            {isEmbedMode() && <StaffScopeBar />}
            {/* ContentHub stays mounted across navigations so its `<img>` thumbnails
                remain in the live DOM; the browser then doesn't refetch preview
                URLs when the user returns to /content-hub. */}
            <KeepMounted active={currentPage === 'content-hub'}>
              <ContentHubPage previewMediaId={contentHubMediaId} navigate={navigate} />
            </KeepMounted>
            {coworkBlocked ? null : currentPage === 'campaign-plan' ? (
              <CampaignWizard />
            ) : currentPage === 'agent-desk' ? (
              <div className="p-6 max-w-6xl mx-auto">
                <CMDesk asPage />
              </div>
            ) : currentPage === 'agent-media-plans' ? (
              <MovedToBackOffice what="Media plans" path={`${location.pathname}${location.search}`} />
            ) : currentPage === 'agent-competitor-ads' ? (
              <CompetitorAdsPage />
            ) : currentPage === 'agent-ads-library' ? (
              <AdsLibraryPage />
            ) : currentPage === 'agent-radar-library' ? (
              <RadarLibraryPage />
            ) : currentPage === 'agent-canvas' ? (
              <CampaignCanvasPage />
            ) : currentPage === 'agent-tiktok-trends' ? (
              <TikTokTrendsPage />
            ) : currentPage === 'agent-knowledge' ? (
              <KnowledgePage />
            ) : currentPage === 'agent-kickoff' ? (
              <MovedToBackOffice what="Kickoff" path={`${location.pathname}${location.search}`} />
            ) : currentPage === 'agent-deliverables' ? (
              <DeliverablesPage />
            ) : currentPage === 'agent-reports' ? (
              <ReportsPage />
            ) : currentPage === 'agent-video-studio' ? (
              <VideoStudioPage />
            ) : isAgentPage(currentPage) ? (
              <AgentSurface view={AGENT_PAGE_VIEW[currentPage]} />
            ) : currentPage === 'dashboard' ? (
              <DashboardPage
                userName={userName}
                accountManager={dashboardAccountManager}
                onNavigate={handleNavigate}
              />
            ) : currentPage === 'news-monitor' ? (
              <div className="relative">
                <NewsMonitorPage
                  articles={newsArticles}
                  onArticleClick={(article) => setSelectedArticle(article)}
                  onToggleRead={handleToggleRead}
                />
                {selectedArticle && (
                  <NewsDetailSidebar
                    article={selectedArticle}
                    onClose={() => setSelectedArticle(null)}
                    onToggleRead={() => handleToggleRead(selectedArticle.id)}
                  />
                )}
              </div>
            ) : currentPage === 'performance' ? (
              <DashboardsPage />
            ) : currentPage === 'brand-pulse' ? (
              <BrandPulsePage />
            ) : currentPage === 'marketing-mix-model-mmm' ? (
              <MarketingMixModelPage />
            ) : currentPage === 'creators' ||
              currentPage === 'search-results' ||
              currentPage === 'creator-profile' ||
              currentPage === 'discovery-creatorlist' ? (
              <DiscoveryEmbedPage />
            ) : currentPage === 'creatorslist' ? (
              <CreatorsListPage />
            ) : currentPage === 'creatorlist-detail' ? (
              <CreatorListDetailPage listId={listId} onNavigate={handleNavigate} />
            ) : currentPage === 'audience-insights' ? (
              <InsightsPage />
            ) : currentPage === 'trends' ? (
              <TrendsPage navigate={navigate} />
            ) : currentPage === 'see-campaigns' ? (
              <Suspense
                fallback={
                  <div
                    className="flex min-h-[50vh] flex-col items-center justify-center gap-3"
                    role="status"
                    aria-live="polite"
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-[#be43ff]" aria-hidden />
                    <span className="sr-only">{t('campaigns.loadingCampaignDetailSr')}</span>
                  </div>
                }
              >
                <SeeCampaignsPage
                  campaignId={campaignId}
                  subTab={subTab}
                  beatId={beatId}
                  onNavigate={handleNavigate}
                  navigate={navigate}
                />
              </Suspense>
            ) : currentPage === 'campaign-calendar' ? (
              <CampaignCalendarPage />
            ) : currentPage === 'ambassador-hub' ? (
              <AmbassadorsPage />
            ) : currentPage === 'drafts' ? (
              <DraftsPage />
            ) : currentPage === 'casting' ? (
              <CastingPage />
            ) : currentPage === 'content-hub' ? (
              null
            ) : currentPage === 'ad-studios' ? (
              <AdStudioLandingPage onNavigate={handleNavigate} />
            ) : currentPage === 'audio-dubbing' ? (
              <DubbingPage onNavigate={handleNavigate} />
            ) : currentPage === 'video-ad-studio' ? (
              <VideoAdStudioPage />
            ) : currentPage === 'audio-studio' ? (
              <AudioStudioGate onNavigate={handleNavigate} />
            ) : currentPage === 'strategies' ? (
              <StrategiesPage />
            ) : currentPage === 'campaigns' ? (
              <div className="w-full h-screen">
                <iframe
                  src="https://planner.beatly.com"
                  className="w-full h-full border-0"
                  title={t('nav.campaignPlannerIframeTitle')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : currentPage === 'messages' ? (
              <MessagesPage />
            ) : currentPage === 'brand-lift' ? (
              <BrandLiftPage />
            ) : currentPage === 'settings' ? (
              <SettingsPage userName={userName} />
            ) : currentPage === 'team' ? (
              <TeamPage />
            ) : currentPage === 'ad-accounts' ? (
              // Meta/TikTok OAuth return uses /ad-accounts/connect?session=… (same page shell)
              <AdAccountsPage />
            ) : currentPage === 'trophies' ? (
              <TrophyRoomPage trophyId={trophyId} onNavigate={handleNavigate} navigate={navigate} />
            ) : currentPage === 'dev-mode' ? (
              <DevModePage />
            ) : null}
          </div>
        </div>
        </>
        )}
      </div>

      {/* Agent rail — the 24/7 assistant, on every platform page */}
      {showCowork && <AgentRail />}

      {/* Creator Profile Sidebar */}
      <CreatorProfileSidebar
        creator={selectedCreator}
        isOpen={creatorProfileOpen}
        onClose={handleCloseCreatorProfile}
      />
    </div>
  );
}
